import React, { useState, useEffect } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";

const TextSlider = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (data.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [data.length]);

  const currentItem = data[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? data.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % data.length);
  };

  // Create gradient from the background color
  const gradientBackground = `linear-gradient(135deg, 
    ${currentItem.backgroundColor} 0%, 
    ${darkenColor(currentItem.backgroundColor, 20)} 100%)`;

  return (
    <Box
      sx={{
        position: "relative",
        width: "90%",
        margin: "auto",
        marginTop: "1rem",
        overflow: "hidden",
        borderRadius: "0.5rem",
        background: gradientBackground,
        color: "white",
        padding: isMobile ? "1rem" : "1.5rem",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
    >
      {/* Invisible left navigation area */}
      {data.length > 1 && (
        <Box
          onClick={goToPrevious}
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "30%",
            cursor: "pointer",
            zIndex: 10,
          }}
        />
      )}

      {/* Invisible right navigation area */}
      {data.length > 1 && (
        <Box
          onClick={goToNext}
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "30%",
            cursor: "pointer",
            zIndex: 10,
          }}
        />
      )}

      {/* Stationary Topic */}
      <Box
        sx={{
          fontSize: isMobile ? "0.8rem" : "1rem",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "1px",
          marginBottom: "0.5rem",
          opacity: 0.8,
          width: "100%",
          textAlign: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        BANILUX GUEST HOUSE
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "1rem 0",
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Title */}
        <Box
          sx={{
            fontSize: isMobile ? "1.2rem" : "1.5rem",
            fontWeight: 600,
            marginBottom: "0.5rem",
            lineHeight: 1.3,
          }}
        >
          {currentItem.title}
        </Box>

        {/* Points with hyphens */}
        <Box
          component="ul"
          sx={{
            paddingLeft: 0,
            margin: 0,
            fontSize: isMobile ? "0.9rem" : "1rem",
            "& li": {
              marginBottom: "0.25rem",
              position: "relative",
              listStyleType: "none",
              display: "flex",
              alignItems: "flex-start",
              lineHeight: 1.4,
            },
            "& li:before": {
              content: '"-"',
              marginRight: "0.5rem",
            },
          }}
        >
          {currentItem.points.map((point, index) => (
            <li key={index}>{point}</li>
          ))}
        </Box>
      </Box>

      {/* Indicator dots */}
      {data.length > 1 && (
        <Box
          sx={{
            position: "absolute",
            bottom: "0.75rem",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            gap: "0.5rem",
            zIndex: 5,
          }}
        >
          {data.map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor:
                  index === currentIndex ? "white" : "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.8)",
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Helper function to darken a color
function darkenColor(color, percent) {
  if (color.startsWith("#") && color.length === 7) {
    const num = parseInt(color.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
    const B = Math.max(0, (num & 0x0000ff) - amt);
    return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
  }
  return color;
}

export default TextSlider;
